'use client';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Button, Input, Card } from '@ui';

const DAY_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

function fmt(d: Date | null) {
  if (!d) return '';
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const da = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${da}`;
}

/** Parses `YYYY-MM-DD` in local calendar; returns `null` for empty/invalid. */
function parseYmd(s: string | null | undefined): Date | null {
  if (s == null) return null;
  const t = String(s).trim();
  if (t === '') return null;
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(t);
  if (!m) return null;
  const y = Number(m[1]);
  const mo = Number(m[2]) - 1;
  const d = Number(m[3]);
  const dt = new Date(y, mo, d);
  if (dt.getFullYear() !== y || dt.getMonth() !== mo || dt.getDate() !== d) return null;
  return startOfDay(dt);
}

function sameDay(a: Date | null, b: Date | null) {
  return (
    !!a &&
    !!b &&
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

function startOfDay(d: Date) {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
}

type Cell = { date: Date; out: boolean };

function buildCells(cursor: Date): Cell[] {
  const y = cursor.getFullYear();
  const m = cursor.getMonth();
  const first = new Date(y, m, 1);
  const startDay = first.getDay();
  const daysInMonth = new Date(y, m + 1, 0).getDate();
  const prevDays = new Date(y, m, 0).getDate();

  const cells: Cell[] = [];
  for (let i = startDay - 1; i >= 0; i--) {
    const d = new Date(y, m - 1, prevDays - i);
    cells.push({ date: d, out: true });
  }
  for (let i = 1; i <= daysInMonth; i++) {
    const d = new Date(y, m, i);
    cells.push({ date: d, out: false });
  }
  while (cells.length % 7 !== 0) {
    const nextIndex = cells.length - startDay - daysInMonth + 1;
    const d = new Date(y, m + 1, nextIndex);
    cells.push({ date: d, out: true });
  }
  return cells;
}

function monthStartForInitial(
  isControlled: boolean,
  value: string | null | undefined,
  defaultValue: string | null | undefined
): Date {
  const initial = isControlled ? parseYmd(value ?? null) : parseYmd(defaultValue ?? null);
  if (initial) return new Date(initial.getFullYear(), initial.getMonth(), 1);
  const n = new Date();
  return new Date(n.getFullYear(), n.getMonth(), 1);
}

export type DatePickerProps = {
  id?: string;
  /** `YYYY-MM-DD` or `null` for no date. When set, the picker is controlled. */
  value?: string | null;
  /** Uncontrolled initial date (`YYYY-MM-DD` or `null`). Ignored when `value` is provided. */
  defaultValue?: string | null;
  onChange?: (value: string | null) => void;
  disabled?: boolean;
};

const DatePicker: React.FC<DatePickerProps> = ({
  id = 'date-picker',
  value,
  defaultValue,
  onChange,
  disabled = false,
}) => {
  const isControlled = value !== undefined;
  const [uncontrolledSelected, setUncontrolledSelected] = useState<Date | null>(() =>
    isControlled ? null : parseYmd(defaultValue ?? null)
  );
  const [cursor, setCursor] = useState(() =>
    monthStartForInitial(isControlled, value, defaultValue)
  );
  const [focusedDate, setFocusedDate] = useState<Date | null>(null);

  const selected = isControlled ? parseYmd(value ?? null) : uncontrolledSelected;

  const commit = useCallback(
    (next: Date | null) => {
      const s = next ? fmt(next) : null;
      onChange?.(s);
      if (!isControlled) {
        setUncontrolledSelected(next);
      }
    },
    [isControlled, onChange]
  );

  useEffect(() => {
    if (!isControlled) return;
    const d = parseYmd(value ?? null);
    if (!d) return;
    setCursor((c) => {
      const m = d.getFullYear() * 12 + d.getMonth();
      const cm = c.getFullYear() * 12 + c.getMonth();
      if (m === cm) return c;
      return new Date(d.getFullYear(), d.getMonth(), 1);
    });
  }, [isControlled, value]);

  const cells = useMemo(() => buildCells(cursor), [cursor]);

  const cellRefs = useRef<Map<string, HTMLDivElement>>(new Map());

  // focus moved when focusedDate changes
  useEffect(() => {
    if (disabled) return;
    if (!focusedDate) return;
    const el = cellRefs.current.get(focusedDate.toISOString());
    if (el) {
      try {
        el.focus({ preventScroll: true });
      } catch {
        el.focus();
      }
    }
  }, [disabled, focusedDate]);

  // init focusedDate to today in view or first day
  useEffect(() => {
    if (disabled) return;
    const today = startOfDay(new Date());
    const todayInView = cells.find((c) => !c.out && sameDay(c.date, today))?.date;
    if (todayInView) setFocusedDate(todayInView);
    else setFocusedDate(cells.find((c) => !c.out)?.date || new Date());
  }, [disabled, cells]);

  const onPrev = useCallback(() => {
    if (disabled) return;
    setCursor((c) => new Date(c.getFullYear(), c.getMonth() - 1, 1));
  }, [disabled]);
  const onNext = useCallback(() => {
    if (disabled) return;
    setCursor((c) => new Date(c.getFullYear(), c.getMonth() + 1, 1));
  }, [disabled]);

  const onSelect = useCallback(
    (date: Date) => {
      if (disabled) return;
      commit(startOfDay(date));
    },
    [commit, disabled]
  );

  const onToday = useCallback(() => {
    if (disabled) return;
    const t = startOfDay(new Date());
    setCursor(new Date(t.getFullYear(), t.getMonth(), 1));
    commit(t);
    setFocusedDate(t);
  }, [commit, disabled]);

  const onClear = useCallback(() => {
    if (disabled) return;
    commit(null);
  }, [commit, disabled]);

  const moveFocusBy = useCallback(
    (base: Date, deltaDays: number) => {
      if (disabled) return;
      const next = new Date(base);
      next.setDate(next.getDate() + deltaDays);
      if (next.getMonth() !== cursor.getMonth() || next.getFullYear() !== cursor.getFullYear()) {
        setCursor(new Date(next.getFullYear(), next.getMonth(), 1));
      }
      setFocusedDate(next);
    },
    [cursor, disabled]
  );

  const onGridKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      if (disabled) return;
      if (!focusedDate) return;
      const key = e.key;
      let handled = true;
      if (key === 'ArrowLeft') moveFocusBy(focusedDate, -1);
      else if (key === 'ArrowRight') moveFocusBy(focusedDate, 1);
      else if (key === 'ArrowUp') moveFocusBy(focusedDate, -7);
      else if (key === 'ArrowDown') moveFocusBy(focusedDate, 7);
      else if (key === 'Home') {
        const day = focusedDate.getDay();
        moveFocusBy(focusedDate, -day);
      } else if (key === 'End') {
        const day = focusedDate.getDay();
        moveFocusBy(focusedDate, 6 - day);
      } else if (key === 'Enter' || key === ' ') {
        onSelect(focusedDate);
      } else handled = false;
      if (handled) e.preventDefault();
    },
    [disabled, focusedDate, moveFocusBy, onSelect]
  );

  const today = useMemo(() => startOfDay(new Date()), []);

  return (
    <Card
      className='drp'
      id={id}
      aria-label='Calendar'
      aria-disabled={disabled || undefined}
      data-disabled={disabled || undefined}
    >
      <div className='drp-inputs'>
        <Input
          id={`${id}-input`}
          placeholder='YYYY-MM-DD'
          readOnly
          disabled={disabled}
          value={fmt(selected)}
        />
        <Button
          type='button'
          label='Today'
          className='btn-outline-tertiary'
          id={`${id}-today`}
          disabled={disabled}
          onClick={onToday}
        />
        <Button
          type='button'
          label='Clear'
          className='btn-outline-tertiary'
          id={`${id}-clear`}
          disabled={disabled}
          onClick={onClear}
        />
      </div>
      <div className='drp-cal'>
        <div className='drp-nav'>
          <Button
            type='button'
            label='‹'
            className='btn-outline-tertiary btn-sm'
            data-prev
            disabled={disabled}
            onClick={onPrev}
          />
          <b className='drp-title'>
            {cursor.toLocaleString(undefined, {
              month: 'long',
              year: 'numeric',
            })}
          </b>
          <Button
            type='button'
            label='›'
            className='btn-outline-tertiary btn-sm'
            data-next
            disabled={disabled}
            onClick={onNext}
          />
        </div>

        <div
          className='drp-dates'
          role='grid'
          tabIndex={disabled ? -1 : 0}
          aria-label='Calendar'
          aria-describedby='drp-instructions'
          onKeyDown={onGridKeyDown}
        >
          {/* header */}
          <div role='row' className='drp-row drp-head-row' aria-rowindex={1}>
            {DAY_NAMES.map((n, idx) => (
              <div key={n} role='columnheader' aria-colindex={idx + 1} className='drp-cell'>
                {n}
              </div>
            ))}
          </div>

          {/* weeks */}
          {(() => {
            const weeks: Cell[][] = [];
            for (let i = 0; i < cells.length; i += 7) weeks.push(cells.slice(i, i + 7));
            return weeks.map((week, rowIndex) => (
              <div key={rowIndex} role='row' className='drp-row' aria-rowindex={rowIndex + 2}>
                {week.map(({ date, out }, colIndex) => {
                  const classes = ['drp-cell'];
                  if (out) classes.push('drp-out');
                  if (sameDay(date, today)) classes.push('drp-today');
                  if (sameDay(date, selected)) classes.push('drp-selected');

                  const iso = date.toISOString();
                  const isFocused = focusedDate ? sameDay(date, focusedDate) : false;

                  return (
                    <div
                      key={iso}
                      ref={(el) => {
                        if (el) cellRefs.current.set(iso, el);
                        else cellRefs.current.delete(iso);
                      }}
                      role='gridcell'
                      aria-colindex={colIndex + 1}
                      aria-label={date.toLocaleString(undefined, {
                        weekday: 'long',
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric',
                      })}
                      tabIndex={isFocused && !disabled ? 0 : -1}
                      aria-selected={sameDay(date, selected)}
                      aria-disabled={out || disabled}
                      className={classes.join(' ')}
                      data-date={iso}
                      onClick={() => !out && !disabled && onSelect(date)}
                    >
                      {date.getDate()}
                    </div>
                  );
                })}
              </div>
            ));
          })()}
        </div>
      </div>
    </Card>
  );
};

export default DatePicker;
